import * as React from "react"
import * as ReactDOM from "react-dom"

import ReportingErrorBoundary from "./ReportingErrorBoundary"
import SubscriptionFormWrapper from "./SubscriptionForm"
import EmailPreferenceCenter from "./EmailPreferenceCenter"
import { EmailPreferenceCenterTranslation } from "./i18n"

interface ModuleEntryInterface {
  type: ModuleEntryType
  selector: string
}

// New module types must be added here
export enum ModuleEntryType {
  SubscriptionForm = "subscription-form",
  EmailPreferenceCenter = "email-preference-center",
}

// Module types must have a defined interface that extends `ModuleEntryInterface`
export interface SubscriptionFormEntry extends ModuleEntryInterface {
  type: ModuleEntryType.SubscriptionForm
  auth: {
    key: string
    secret: string
  }
  unsubscribeRedirectUrl: string
}

export interface EmailPreferenceCenterEntry extends ModuleEntryInterface {
  type: ModuleEntryType.EmailPreferenceCenter
  auth: {
    key: string
    secret: string
  }
  id: string
  i18n?: { [key: string]: Partial<EmailPreferenceCenterTranslation> }
  unsubscribeRedirectUrl?: string
}

// And that module interface must be added to this union type
export type ModuleEntry = SubscriptionFormEntry | EmailPreferenceCenterEntry

const ComponentLoader: React.FC<ModuleEntry> = (entry) => {
  // Module type must be handled here, by returning a React element
  // (default intentionally left off to enforce exhaustiveness)
  switch (entry.type) {
    case ModuleEntryType.SubscriptionForm:
      return <SubscriptionFormWrapper {...entry} />
    case ModuleEntryType.EmailPreferenceCenter:
      return <EmailPreferenceCenter {...entry} />
  }
}

export default function bootstrap(moduleEntry: ModuleEntry): void {
  const element = document.querySelector(moduleEntry.selector)

  if (!element)
    throw new Error(
      `Element matching selector ${moduleEntry.selector} not found for module type ${moduleEntry.type}`
    )

  ReactDOM.render(
    <ReportingErrorBoundary fallback="An error was encountered, refresh and try again.">
      <ComponentLoader {...moduleEntry} />
    </ReportingErrorBoundary>,
    element
  )
}

export function isModuleEntry(obj: unknown): obj is ModuleEntry {
  if (!isRecord(obj)) return false
  if (!Object.values(ModuleEntryType).includes(obj.type as ModuleEntryType))
    return false
  if (typeof obj.selector !== "string") return false

  return true
}

function isRecord(obj: unknown): obj is Record<string, unknown> {
  if (typeof obj !== "object") return false
  if (obj === null) return false
  return Object.keys(obj).every((key) => typeof key === "string")
}
